import { SessionData } from "@shared/schema";
import { getCreditBalance } from "./api";
import { isAuthenticated } from "./supabase";

const SESSION_KEY = "photoEditSession";

const DEFAULT_SESSION: SessionData = {
  credits: 0, // Will be fetched from API
  onboardingComplete: false,
  postsCreated: 0,
};

// Get session from localStorage (cache)
function getLocalSession(): SessionData {
  const stored = localStorage.getItem(SESSION_KEY);
  if (!stored) {
    return DEFAULT_SESSION;
  }
  try {
    return { ...DEFAULT_SESSION, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_SESSION;
  }
}

// Get session (fetches from API)
export async function getSessionAsync(): Promise<SessionData> {
  // Check if authenticated
  if (!(await isAuthenticated())) {
    return DEFAULT_SESSION;
  }

  try {
    // Fetch credits from API
    const creditBalance = await getCreditBalance();
    const localSession = getLocalSession();

    const session = {
      credits: creditBalance.credit_balance,
      onboardingComplete: localSession.onboardingComplete,
      postsCreated: localSession.postsCreated,
    };

    // Cache in localStorage
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    
    return session;
  } catch (error) {
    console.error('Failed to fetch session from API:', error);
    // Return cached data if API fails
    return getLocalSession();
  }
}

// Synchronous version (returns cached data)
export function getSession(): SessionData {
  return getLocalSession();
}

// Refresh session from API
export async function refreshSession(): Promise<SessionData> {
  const session = await getSessionAsync();
  return session;
}

export function updateSession(updates: Partial<SessionData>): SessionData {
  const current = getLocalSession();
  const updated = { ...current, ...updates };
  localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
  return updated;
}

export function addCredits(amount: number): SessionData {
  const current = getLocalSession();
  return updateSession({ credits: current.credits + amount });
}

export function completeOnboarding(): SessionData {
  return updateSession({ onboardingComplete: true });
}

export function incrementPostsCreated(): SessionData {
  const current = getLocalSession();
  return updateSession({ postsCreated: current.postsCreated + 1 });
}
